import { databases, DATABASE_ID, COLLECTIONS, Query } from "./appwrite";

export const QUESTIONS_COLLECTION = COLLECTIONS.QUESTIONS;

export interface DBQuestion {
  $id: string;
  subjectId: string;
  moduleId: string;
  topicId: string;
  subtopicId?: string;
  skill?: string;
  passage?: string;
  passageImage?: string;
  question: string;
  questionImage?: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctAnswer: "A" | "B" | "C" | "D";
  explanation: string;
  difficulty: "easy" | "medium" | "hard";
  category?: string;
  isActive: boolean;
  createdAt: string;
}

const APPWRITE_ENDPOINT = "https://nyc.cloud.appwrite.io/v1";
const APPWRITE_PROJECT = "694a25b30004a612d2ff";
const STORAGE_BUCKET = "question-images";

export function getStorageFileUrl(fileId: string): string {
  return `${APPWRITE_ENDPOINT}/storage/buckets/${STORAGE_BUCKET}/files/${fileId}/view?project=${APPWRITE_PROJECT}`;
}

export interface Question {
  id: number | string;
  passage?: string;
  passageImage?: string;
  question: string;
  questionImage?: string;
  options: { id: string; text: string; isCorrect: boolean }[];
  explanation: string;
  category?: string;
  skill?: string;
  difficulty?: "easy" | "medium" | "hard";
}

function transformQuestion(dbQuestion: DBQuestion): Question {
  return {
    id: dbQuestion.$id,
    passage: dbQuestion.passage || undefined,
    passageImage: dbQuestion.passageImage ? getStorageFileUrl(dbQuestion.passageImage) : undefined,
    question: dbQuestion.question,
    questionImage: dbQuestion.questionImage ? getStorageFileUrl(dbQuestion.questionImage) : undefined,
    options: [
      { id: "A", text: dbQuestion.optionA, isCorrect: dbQuestion.correctAnswer === "A" },
      { id: "B", text: dbQuestion.optionB, isCorrect: dbQuestion.correctAnswer === "B" },
      { id: "C", text: dbQuestion.optionC, isCorrect: dbQuestion.correctAnswer === "C" },
      { id: "D", text: dbQuestion.optionD, isCorrect: dbQuestion.correctAnswer === "D" },
    ],
    explanation: dbQuestion.explanation,
    category: dbQuestion.category || undefined,
    skill: dbQuestion.skill || undefined,
    difficulty: dbQuestion.difficulty,
  };
}

export async function loadQuestionsFromDB(
  subjectId: string,
  moduleId: string,
  options?: {
    topicId?: string;
    subtopicId?: string;
    difficulty?: "easy" | "medium" | "hard";
    limit?: number;
    shuffle?: boolean;
  }
): Promise<Question[]> {
  try {
    const queries = [
      Query.equal("subjectId", subjectId),
      Query.equal("moduleId", moduleId),
      Query.equal("isActive", true),
    ];

    if (options?.topicId) queries.push(Query.equal("topicId", options.topicId));
    if (options?.subtopicId) queries.push(Query.equal("subtopicId", options.subtopicId));
    if (options?.difficulty) queries.push(Query.equal("difficulty", options.difficulty));

    queries.push(Query.limit(options?.limit || 200));

    const response = await databases.listDocuments(DATABASE_ID, QUESTIONS_COLLECTION, queries);
    let questions = response.documents.map((doc) => transformQuestion(doc as unknown as DBQuestion));

    if (options?.shuffle) questions = shuffleArray(questions);

    return questions;
  } catch (error: any) {
    console.error("Error loading questions from DB:", error);
    if (error.code === 404) {
      console.warn("⚠️ Questions collection not found. Create it in Appwrite Console.");
    }
    return [];
  }
}

export async function getQuestionCountFromDB(
  subjectId: string,
  moduleId?: string,
  topicId?: string
): Promise<number> {
  try {
    const queries = [Query.equal("subjectId", subjectId), Query.equal("isActive", true)];
    if (moduleId) queries.push(Query.equal("moduleId", moduleId));
    if (topicId) queries.push(Query.equal("topicId", topicId));
    queries.push(Query.limit(1));

    const response = await databases.listDocuments(DATABASE_ID, QUESTIONS_COLLECTION, queries);
    return response.total;
  } catch {
    return 0;
  }
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
